import {
  ATTRIBUTION_PARAM_KEYS,
  type AttributionCaptureReq,
  type AttributionParams,
} from "@/types/acquisition.ts";

/**
 * The campaign parameters this page load arrived with, shaped for
 * `POST /api/p/attribution`, or null when it carries none.
 *
 * Takes the first value of a repeated key (`URLSearchParams.get`), so a
 * repeated parameter can't smuggle in a second value. The referrer
 * is sent only when it is another site, and only as its origin, so a path or
 * query on the linking page never leaves the browser.
 */
export function readAttributionCapture(
  location: Pick<Location, "search" | "pathname" | "origin">,
  referrer: string,
): AttributionCaptureReq | null {
  const search = new URLSearchParams(location.search);
  const params: Record<string, string> = {};
  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const value = search.get(key);
    if (value !== null) params[key] = value;
  }
  if (Object.keys(params).length === 0) return null;

  const capture: AttributionCaptureReq = { params: params satisfies AttributionParams };
  // A share link carries its bearer token in the path, so it must not be recorded.
  if (!location.pathname.startsWith("/shared/")) capture.landingPage = location.pathname;
  const referrerOrigin = crossOriginReferrer(location.origin, referrer);
  if (referrerOrigin !== undefined) capture.referrer = referrerOrigin;
  return capture;
}

function crossOriginReferrer(ownOrigin: string, referrer: string): string | undefined {
  if (referrer === "") return undefined;
  try {
    const { origin } = new URL(referrer);
    // An opaque origin ("null") has nothing worth recording.
    if (origin === "null" || origin === ownOrigin) return undefined;
    return origin;
  } catch {
    return undefined;
  }
}
